/*
 Re-renders a bundle product's carousel when the variant changes.

 A bundle's images live on each variant (custom.bundle_images), so the carousel has to change with the selection. The markup for a variant is rendered by Liquid through the Section Rendering API and kept, so each variant is fetched at most once and returning to one already seen is immediate.

 The element replaces itself with the newly rendered one. That also drops the media cache the theme's own variant filter keys on the old element, so the two can't end up fighting over the slides.
*/
if (!customElements.get('bundle-product-media')) {
  /*
    variant id -> {
      media: outerHTML of the carousel,
      modal: innerHTML of the zoom slides
    }
  */
  const rendered = new Map();

  class BundleProductMedia extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url;
      this.variantId = this.dataset.variantId;

      if (this.variantId && !rendered.has(this.variantId)) {
        const modal = this.modalSlides;
        rendered.set(this.variantId, {
          media: this.outerHTML,
          modal: modal ? modal.innerHTML : null
        });
      }

      this.onChange = this.onChange.bind(this);
      document.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onChange);
    }

    get modalSlides() {
      return document.querySelector(
        `#ProductModal-${this.sectionId} .js-popup-slider .swiper-wrapper`
      );
    }

    /*
      The theme writes the selection to the product form's hidden input and then to the URL. Both are read, so a form whose id doesn't line up with the section can't silently stop the carousel updating.
    */
    currentVariantId() {
      const input =
        document.querySelector(`#product-form-${this.sectionId} input[name="id"]`) ||
        (this.section && this.section.querySelector('input[name="id"]'));

      if (input && input.value) return input.value;

      const fromUrl = new URLSearchParams(window.location.search).get('variant');
      if (fromUrl) return fromUrl;

      console.warn(
        `[bundle-product-media] no selected variant to read: looked for #product-form-${this.sectionId} input[name="id"] and a ?variant= parameter`
      );
      return null;
    }

    get section() {
      return this.closest('.product-section') || this.closest('.shopify-section');
    }

    onChange() {
      const variantId = this.currentVariantId();

      if (!variantId || variantId === this.variantId) return;

      this.variantId = variantId;

      const cached = rendered.get(variantId);
      if (cached) {
        this.apply(variantId, cached);
        return;
      }

      this.fetchVariant(variantId);
    }

    fetchVariant(variantId) {
      fetch(`${this.productUrl}?variant=${variantId}&section_id=${this.sectionId}`)
        .then(response => response.text())
        .then(text => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const media = html.querySelector('bundle-product-media');

          if (!media) {
            console.warn(
              '[bundle-product-media] the re-rendered section has no bundle carousel: the bundle_variant_picker block is what marks a product page as a bundle'
            );
            return;
          }

          const modal = html.querySelector(
            `#ProductModal-${this.sectionId} .js-popup-slider .swiper-wrapper`
          );

          const entry = { media: media.outerHTML, modal: modal ? modal.innerHTML : null };
          rendered.set(variantId, entry);
          this.apply(variantId, entry);
        })
        .catch(error => {
          console.warn('[bundle-product-media] could not re-render the carousel', error);
        });
    }

    apply(variantId, entry) {
      if (variantId !== this.variantId || !this.isConnected) return;

      const template = document.createElement('template');
      template.innerHTML = entry.media;
      const fresh = template.content.firstElementChild;
      if (!fresh) return;

      const sectionId = this.sectionId;
      const section = this.section;

      /*
        The zoom slides live outside this element, so the modal slider is torn down before they are swapped and rebuilt afterwards.
      */
      const popup = document.querySelector(`#ProductModal-${sectionId} .js-popup-slider`);
      if (popup && popup.swiper) popup.swiper.destroy(true, true);

      const modalSlides = this.modalSlides;
      if (entry.modal !== null && modalSlides) modalSlides.innerHTML = entry.modal;

      this.replaceWith(fresh);

      if (typeof reinitProductSwipers === 'function') reinitProductSwipers(section);
      if (typeof popupSliderInit === 'function') popupSliderInit(section);
    }
  }

  customElements.define('bundle-product-media', BundleProductMedia);
}
